import { forwardRef, ReactNode } from 'react'

import { Button, ButtonProps, Text } from '@chakra-ui/react'

interface Props extends ButtonProps {
    title?: string
    view: 'primary' | 'secondary' | 'error' | 'success' | 'ghost' | 'warning'
    size: 'sm' | 'md' | 'lg' | 'xs'
    children?: ReactNode
    hideTitle?: boolean
}

export const MainButton = forwardRef<HTMLButtonElement, Props>(
    ({ title, view, size, children, hideTitle, ...props }, ref) => {
        const getSize = () => {
            switch (size) {
                case 'xs':
                    return '28px'
                case 'sm':
                    return '36px'
                case 'md':
                    return '40px'
                case 'lg':
                    return '44px'
                default:
                    return '40px'
            }
        }

        const defaultStyles: ButtonProps = {
            variant: 'unstyled',
            minW: '80px',
            h: getSize(),
            borderRadius: '6px',
            fontSize: 'sm',
            fontWeight: 'medium',
            letterSpacing: '0.16px',
            padding: '12px 20px',
            lineHeight: '20px',
            display: 'flex',
            alignItems: 'center',
            transition: 'all .3s ease',
            _focus: { boxShadow: '0 0 0 2px #245AF066' },
            _disabled: {
                opacity: 0.5,
                bg: '#F5F5F5',
                color: '#0C0C0D',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                svg: { color: '#0C0C0D' },
                _hover: { bg: '#F5F5F5' },
                cursor: 'not-allowed',
            },
        }

        const getViewStyles = (): ButtonProps => {
            switch (view) {
                case 'primary':
                    return {
                        bg: '#245AF0',
                        color: '#FFF',
                        _hover: { bg: '#1D48C0' },
                        _active: { bg: '#163690' },
                    }
                case 'secondary':
                    return {
                        border: '1px solid #CBCBCD',
                        bg: '#FFF',
                        color: '#0C0C0D',
                        _hover: { bg: '#F5F5F5' },
                        _active: { bg: '#E0E0E1' },
                    }
                case 'error':
                    return {
                        bg: '#DB3333',
                        color: '#FFF',
                        _hover: { bg: '#B62626' },
                        _active: { bg: '#881C1C' },
                    }
                case 'success':
                    return {
                        bg: '#00853D',
                        color: '#FFF',
                        _hover: { bg: '#006D33' },
                        _active: { bg: '#005226' },
                    }
                case 'ghost':
                    return {
                        bg: 'transparent',
                        borderColor: 'transparent',
                        color: '#0C0C0D',
                        _hover: { bg: '#F5F5F5' },
                        _active: { bg: '#E0E0E1' },
                    }
                case 'warning': {
                    return {
                        bg: '#F07524',
                        color: '#FFF',
                        _hover: { bg: '#C05E1D' },
                        _active: { bg: '#904616' },
                    }
                }
                default:
                    return {}
            }
        }

        return (
            <Button
                translate={'no'}
                className={'notranslate'}
                ref={ref}
                {...defaultStyles}
                {...getViewStyles()}
                {...props}
            >
                {title && !hideTitle && <Text>{title}</Text>}
                {children}
            </Button>
        )
    }
)

MainButton.displayName = 'MainButton'
